import {Utility} from "@reapptor-apps/reapptor-toolkit";
import {ApiProvider, ch, IBaseComponent, PageRoute} from "@reapptor-apps/reapptor-react-common";
import ServiceProvider from "@/models/server/ServiceProvider";
import ApplicationContext from "@/models/server/ApplicationContext";
import AppConstants from "@/helpers/AppConstants";
import {ApplicationType} from "@/models/Enums";
import ListServiceProviderPoliciesRequest from "@/models/server/requests/ListServiceProviderPoliciesRequest";
import ServiceProviderPolicy from "@/models/server/bout/ServiceProviderPolicy";
import ListServiceProviderPoliciesResponse from "@/models/server/responses/ListServiceProviderPoliciesResponse";
import Localizer from "@/localization/Localizer";

class ServiceProviderController {
    private _initialized: boolean = false;
    private _initializing: boolean = false;
    
    public async selectCurrentServiceProviderAsync(serviceProvider: ServiceProvider | null): Promise<void> {
        await this.loadStylesAsync(serviceProvider);
    }
    
    public get isHds(): boolean {
        const serviceProvider: ServiceProvider | null = this.serviceProviderSlug;
        return (serviceProvider?.slug == AppConstants.hdsSlug);
    }
    
    public get isPjta(): boolean {
        const serviceProvider: ServiceProvider | null = this.serviceProviderSlug;
        return (serviceProvider?.slug == AppConstants.pjtaSlug);
    }
    
    public async listServiceProviderPoliciesAsync(caller: IBaseComponent | null = null): Promise<ServiceProviderPolicy[]> {
        const request = new ListServiceProviderPoliciesRequest();
        request.serviceProviderId = this.serviceProviderId;
        
        const response: ListServiceProviderPoliciesResponse = await ApiProvider.postAsync("/api/serviceProvider/listServiceProviderPolicies", request, caller);
        
        return response.policies;
    }

    private async loadStylesAsync(serviceProvider: ServiceProvider | null): Promise<void> {

        const html: HTMLHtmlElement = document.getElementsByTagName("html")[0];

        let newClassName: string = "";

        const className: string = html.getAttribute("class") ?? "";

        let sp: boolean = false;

        if (serviceProvider?.slug) {
            sp = true;

            newClassName = `sp ${serviceProvider.slug}`;
        }

        if (className != newClassName) {
            if (sp) {
                await import("../sp/sp.module.scss");
            }

            html.setAttribute("class", newClassName);
        }
    }

    public async initializeAsync(): Promise<void> {
        if ((!this._initialized) && (!this._initializing)) {
            this._initializing = true;

            try {
                
                if (!ch.isCrawler) {
                    await this.resolveSlugAsync();
                }

                this._initialized = true;
            } finally {
                this._initializing = false;
            }
        }
    }
    
    public get applicationTypes(): ApplicationType[] {
        return this.serviceProviderSlug?.applicationTypes ?? ch.findContext<ApplicationContext>()?.applicationTypes ?? [];
    }
    
    public get serviceProviderSlug(): ServiceProvider | null {
        return ch.findContext<ApplicationContext>()?.serviceProvider || null;
    }
    
    public get serviceProviderId(): string | null {
        return this.serviceProviderSlug?.id || null;
    }
    
    public get supportsRating(): boolean {
        const serviceProvider: ServiceProvider | null = this.serviceProviderSlug;
        return ((serviceProvider == null) || (serviceProvider.supportsRating));
    }

    public get isSingleApplicationType(): boolean {
        return ((this.serviceProviderSlug != null) && (this.serviceProviderSlug.applicationTypes.length == 1));
    }

    public get isShuttleApplicationOnly(): boolean {
        const serviceProviderSlug: ServiceProvider | null = this.serviceProviderSlug;
        return (
            (serviceProviderSlug != null) &&
            (serviceProviderSlug.applicationTypes.length == 1) &&
            (serviceProviderSlug.applicationTypes[0] == ApplicationType.Shuttle)
        );
    }

    public get canCallToCaptainForCompletedTrip(): boolean {
        return (!this.isPjta);
    }

    public get personalInformationWarning(): boolean {
        const serviceProvider: ServiceProvider | null = this.serviceProviderSlug;
        return ((serviceProvider != null) && (serviceProvider.personalInformationWarning));
    }

    public get shuttleMaxTickets(): number {
        return this.serviceProviderSlug?.shuttleMaxTickets ?? AppConstants.defaultShuttleMaxTickets;
    }

    public get minBookingDepthInHours(): number | null {
        return this.serviceProviderSlug?.minBookingDepthInHours ?? null;
    }

    public get maxBookingDepthInHours(): number | null {
        return this.serviceProviderSlug?.maxBookingDepthInHours ?? null;
    }

    public get minBookingRangeInMinutes(): number | null {
        return this.serviceProviderSlug?.minBookingRangeInMinutes ?? null;
    }

    public get maxBookingRangeInMinutes(): number | null {
        return this.serviceProviderSlug?.maxBookingRangeInMinutes ?? null;
    }

    public get supportsNowBooking(): boolean {
        return (this.minBookingDepthInHours == 0);
    }

    public get estimatedBookingExpirationInMinutes(): number | null {
        return this.serviceProviderSlug?.estimatedBookingExpirationInMinutes ?? null;
    }

    public get hasEstimatedBookingExpirationInMinutes(): boolean {
        return ((this.estimatedBookingExpirationInMinutes != null) && (this.estimatedBookingExpirationInMinutes > 0));
    }

    public get bookingWarningIntervalInHours(): number | null {
        return (!this.supportsNowBooking)
            ? AppConstants.bookingWarningIntervalInHours
            : null;
    }

    public get androidAppAvailable(): boolean {
        const serviceProviderSlug: ServiceProvider | null = this.serviceProviderSlug;
        return (serviceProviderSlug == null) || (serviceProviderSlug.androidAppAvailable == true);
    }

    public get appleAppAvailable(): boolean {
        const serviceProviderSlug: ServiceProvider | null = this.serviceProviderSlug;
        return (serviceProviderSlug == null) || (serviceProviderSlug.appleAppAvailable == true);
    }

    public get passengerSignUpAvailable(): boolean {
        const serviceProviderSlug: ServiceProvider | null = this.serviceProviderSlug;
        return (serviceProviderSlug == null) || (serviceProviderSlug.passengerSignUpAvailable == true);
    }

    public get captainSignUpAvailable(): boolean {
        const serviceProviderSlug: ServiceProvider | null = this.serviceProviderSlug;
        return (serviceProviderSlug == null) || (serviceProviderSlug.captainSignUpAvailable == true);
    }

    public get userSignUpAvailable(): boolean {
        return ((this.passengerSignUpAvailable) || (this.captainSignUpAvailable));
    }

    public get applicationName(): string {
        const context: ApplicationContext = ch.getContext();
        const serviceProviderSlug: ServiceProvider | null = this.serviceProviderSlug;
        return serviceProviderSlug?.applicationName || context.applicationName;
    }

    public get applicationDefaultLanguage(): string {
        const serviceProviderSlug: ServiceProvider | null = this.serviceProviderSlug;
        return serviceProviderSlug?.language ?? Localizer.defaultLanguage;
    }
    
    public get applicationUrl(): string {
        const context: ApplicationContext = ch.getContext();
        const serviceProviderSlug: ServiceProvider | null = this.serviceProviderSlug;
        
        if (serviceProviderSlug) {
            if (serviceProviderSlug.applicationUrl) {
                return serviceProviderSlug.applicationUrl;
            }
            
            if (serviceProviderSlug.slug) {
                return `${this.trimUrl(context.applicationUrl)}/${this.trimUrl(serviceProviderSlug.slug)}/`;
            }
        }
        
        return context.applicationUrl;
    }

    private static trim(value: string, trimChars: string[] | string): string {
        if (Array.isArray(trimChars)) {
            const trimCharsLength: number = trimChars.length;
            let i: number = 0;
            while(i < trimCharsLength) {
                const sourceLength: number = value.length;
                value = ServiceProviderController.trim(value, trimChars[i]);
                i = (value.length < sourceLength)
                    ? 0
                    : i + 1;
            }
            return value;
        }

        let include: boolean = true;
        const trimLength: number = trimChars.length;
        while (include) {
            include = false;
            const startIndex: number = value.indexOf(trimChars);
            if (startIndex != -1) {
                if (startIndex == 0) {
                    include = true;
                    value = value.substring(trimLength);
                }

                const endIndex: number = value.lastIndexOf(trimChars);
                const length: number = value.length;
                if (endIndex + trimLength == length) {
                    include = true;
                    value = value.substring(0, length - trimLength);
                }
            }
        }
        
        return value;
    }
    
    public trimUrl(url: string): string {
        return ServiceProviderController.trim(url, ['\\', '/', ' ', '\n', '\r']);
    }
    
    private async resolveSlugAsync(): Promise<void> {
        await this.selectCurrentServiceProviderAsync(this.serviceProviderSlug);
    }
    
    public serviceProviderRouteResolver(): PageRoute | null {
        if (!ch.isCrawler) {
            Utility.background(() => this.resolveSlugAsync());
        }
        return null;
    }
}

//Singleton
export default new ServiceProviderController();